import React, { useEffect } from 'react';
import { X, Printer, MapPin, Clock, User, Activity } from 'lucide-react';

const PrintQueueTicket = ({ 
    queueNumber, 
    patientName, 
    department, 
    nextLocation, 
    nextRoom, 
    locationAddress,
    vitals,
    priority,
    estimatedWait,
    onClose 
}) => {
    
    const handlePrint = () => {
        window.print();
    };
    
    const formatQueueNumber = (num) => {
        return String(num).padStart(3, '0');
    };
    
    const getPriorityBadge = (priority) => {
        const colors = {
            'low': 'bg-green-100 text-green-800',
            'medium': 'bg-yellow-100 text-yellow-800',
            'high': 'bg-red-100 text-red-800',
            'critical': 'bg-red-600 text-white'
        };
        return colors[priority] || colors.medium;
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4">
                {/* Screen View - Not Printed */}
                <div className="print:hidden">
                    <div className="flex justify-between items-center p-4 border-b">
                        <h3 className="text-lg font-semibold text-gray-900">Queue Ticket</h3>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    
                    <div className="flex gap-3 p-4 border-b bg-gray-50">
                        <button
                            onClick={handlePrint}
                            className="flex-1 bg-blue-500 text-white px-4 py-3 rounded-lg hover:bg-blue-600 flex items-center justify-center gap-2 font-medium"
                        >
                            <Printer className="w-5 h-5" />
                            Print Ticket
                        </button>
                        <button
                            onClick={onClose}
                            className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                            Close
                        </button>
                    </div>
                </div>

                {/* Printable Ticket Content */}
                <div className="p-8 print:p-4">
                    {/* Header */}
                    <div className="text-center border-b-2 border-gray-300 pb-4 mb-4">
                        <h1 className="text-2xl font-bold text-blue-600 print:text-black">LIPAY AFYA HOSPITAL</h1>
                        <p className="text-sm text-gray-600">Patient Queue Ticket</p>
                    </div>

                    {/* Queue Number - Prominent */}
                    <div className="text-center mb-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg p-6 print:bg-gray-100 print:text-black print:border-2 print:border-black">
                        <p className="text-sm font-medium opacity-90 print:text-black">YOUR QUEUE NUMBER</p>
                        <p className="text-6xl font-bold mt-2 print:text-5xl">#{formatQueueNumber(queueNumber)}</p>
                    </div>

                    {/* Patient Details */}
                    <div className="mb-4 pb-4 border-b">
                        <div className="flex items-center gap-2 mb-2">
                            <User className="w-4 h-4 text-gray-500" />
                            <p className="text-sm font-medium text-gray-600">Patient Name:</p>
                        </div>
                        <p className="text-lg font-semibold text-gray-900">{patientName}</p>
                        
                        {department && (
                            <div className="mt-2">
                                <p className="text-sm text-gray-600">Department: <span className="font-medium text-gray-900">{department}</span></p>
                            </div>
                        )}
                        
                        {priority && (
                            <div className="mt-2">
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getPriorityBadge(priority)} print:border print:border-black`}>
                                    {priority.toUpperCase()} PRIORITY
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Vitals Summary (if recorded) */}
                    {vitals && (vitals.weight || vitals.bp_systolic || vitals.temperature) && (
                        <div className="mb-4 pb-4 border-b bg-blue-50 p-3 rounded-lg print:bg-white print:border-black">
                            <div className="flex items-center gap-2 mb-2">
                                <Activity className="w-4 h-4 text-blue-600" />
                                <p className="text-sm font-semibold text-gray-900">Vitals Recorded:</p>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                                {vitals.weight && (
                                    <div>• Weight: <span className="font-semibold">{vitals.weight} kg</span></div>
                                )}
                                {vitals.bp_systolic && (
                                    <div>• BP: <span className="font-semibold">{vitals.bp_systolic}/{vitals.bp_diastolic} mmHg</span></div>
                                )}
                                {vitals.temperature && (
                                    <div>• Temp: <span className="font-semibold">{vitals.temperature}°C</span></div>
                                )}
                                {vitals.heart_rate && (
                                    <div>• HR: <span className="font-semibold">{vitals.heart_rate} bpm</span></div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Next Location Instructions */}
                    <div className="mb-4 pb-4 border-b bg-yellow-50 p-4 rounded-lg print:bg-white print:border-2 print:border-black">
                        <div className="flex items-center gap-2 mb-2">
                            <MapPin className="w-5 h-5 text-yellow-600" />
                            <p className="text-sm font-bold text-gray-900">PLEASE GO TO:</p>
                        </div>
                        <p className="text-2xl font-bold text-gray-900 mb-2">
                            {nextLocation?.toUpperCase() || 'TRIAGE'} 
                            {nextRoom && ` - ${nextRoom}`}
                        </p>
                        {locationAddress && (
                            <p className="text-sm text-gray-600 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                Location: {locationAddress}
                            </p>
                        )}
                        <p className="text-sm text-gray-700 mt-3 font-medium">
                            → Wait for your number <span className="font-bold">#{formatQueueNumber(queueNumber)}</span> to be called on the display screen
                        </p>
                    </div>

                    {/* Estimated Wait Time */}
                    {estimatedWait && (
                        <div className="mb-4 pb-4 border-b">
                            <div className="flex items-center gap-2 mb-1">
                                <Clock className="w-4 h-4 text-gray-500" />
                                <p className="text-sm font-medium text-gray-600">Estimated Wait Time:</p>
                            </div>
                            <p className="text-lg font-semibold text-gray-900">{estimatedWait} minutes</p>
                        </div>
                    )}

                    {/* Instructions */}
                    <div className="text-xs text-gray-600 space-y-1">
                        <p>• Please remain in the waiting area</p>
                        <p>• Watch the display screen for your queue number</p>
                        <p>• Keep this ticket with you</p>
                        <p>• If you need assistance, show this ticket to staff</p>
                    </div>

                    {/* Footer */}
                    <div className="mt-6 pt-4 border-t text-center">
                        <p className="text-xs text-gray-500">
                            Checked in: {new Date().toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                            Thank you for choosing Lipay Afya Hospital
                        </p>
                    </div>
                </div>

                {/* Print-specific styles */}
                <style jsx>{`
                    @media print {
                        @page {
                            margin: 0.5in;
                            size: 4in 6in;
                        }
                        body {
                            print-color-adjust: exact;
                            -webkit-print-color-adjust: exact;
                        }
                    }
                `}</style>
            </div>
        </div>
    );
};

export default PrintQueueTicket;

